import { Client } from "@microsoft/microsoft-graph-client";
import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";
import { PublicClientApplication, InteractionType, Configuration } from "@azure/msal-browser";
import { supabase } from './supabase';

// MSAL Configuration
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
    windowHashTimeout: 60000,
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0:
            console.error('[MSAL]', message);
            break;
          case 1:
            console.warn('[MSAL]', message);
            break;
          case 2:
            console.info('[MSAL]', message);
            break;
          case 3:
            console.debug('[MSAL]', message);
            break;
        }
      },
      piiLoggingEnabled: false
    }
  }
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
const initializeMsal = async () => {
  try {
    console.debug('[MSAL] Initializing...', {
      clientId: msalConfig.auth.clientId,
      redirectUri: msalConfig.auth.redirectUri
    });

    await msalInstance.initialize();
    const response = await msalInstance.handleRedirectPromise();
    
    console.debug('[MSAL] Initialization response:', {
      hasResponse: !!response,
      accountCount: msalInstance.getAllAccounts().length
    });

    if (response) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        
        // Store Microsoft account in database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.debug('[MSAL] Storing Microsoft account for user:', {
            userId: user.id,
            microsoftEmail: accounts[0].username
          });

          const { error } = await supabase
            .from('microsoft_accounts')
            .upsert({
              user_id: user.id,
              microsoft_id: accounts[0].localAccountId,
              email: accounts[0].username,
            }, {
              onConflict: 'user_id',
            });

          if (error) {
            console.error('[MSAL] Error storing Microsoft account:', error);
          }
        }
      }
    }
  } catch (error) {
    console.error('[MSAL] Initialization error:', error);
  }
};

initializeMsal();

const getAuthProvider = () => {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0] || null;

  if (!account) {
    throw new Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
  }

  return new AuthCodeMSALBrowserAuthenticationProvider(msalInstance, {
    account,
    scopes: ["Calendars.ReadWrite", "User.Read"],
    interactionType: InteractionType.Popup,
  });
};

export const getGraphClient = () => {
  try {
    const authProvider = getAuthProvider();
    return Client.initWithMiddleware({ authProvider });
  } catch (error) {
    console.error("Error initializing Graph client:", error);
    throw error;
  }
};

// Helper function to get user's timezone from browser
const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn("Could not detect time zone, using fallback", error);
    return "Europe/Berlin"; // Fallback to a common European time zone
  }
};

// Fixed time zone for Germany (Berlin)
const FIXED_TIME_ZONE = "Europe/Berlin";

export const getCalendarEvents = async (startTime: Date, endTime: Date) => {
  try {
    console.debug('[Calendar] Fetching calendar events:', {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString()
    });

    const graphClient = getGraphClient();
    const userTimeZone = FIXED_TIME_ZONE;
    
    // Add one month to the end time to ensure we get all future events
    const extendedEndTime = new Date(endTime);
    extendedEndTime.setMonth(extendedEndTime.getMonth() + 1);
    
    let allEvents = [];
    let nextLink = null;
    
    // Initial request
    let response = await graphClient
      .api("/me/calendarView")
      .query({
        startDateTime: startTime.toISOString(),
        endDateTime: extendedEndTime.toISOString(),
        $orderby: "start/dateTime",
        $select: "id,subject,start,end,location,showAs,sensitivity,organizer,isOrganizer",
        $top: 250,
        Prefer: `outlook.timezone="${userTimeZone}"`
      })
      .get();

    // Add the first batch of events
    allEvents = allEvents.concat(response.value);

    // Get the @odata.nextLink if it exists
    nextLink = response["@odata.nextLink"];

    // Keep fetching while there are more pages
    while (nextLink) {
      console.debug('[Calendar] Fetching next page of events');
      
      response = await graphClient
        .api(nextLink)
        .get();

      allEvents = allEvents.concat(response.value);
      nextLink = response["@odata.nextLink"];
    }

    console.debug('[Calendar] Raw calendar response:', {
      totalEvents: allEvents.length,
      firstEvent: allEvents[0],
      lastEvent: allEvents[allEvents.length - 1]
    });

    // Filter events to only show those where the user is the organizer
    const currentUserEmail = msalInstance.getAllAccounts()[0]?.username.toLowerCase();
    
    const filteredEvents = allEvents
      .filter((event: any) => 
        event.organizer.emailAddress.address.toLowerCase() === currentUserEmail)
      .map((event: any) => ({
        id: event.id,
        subject: event.sensitivity === 'private' ? 'Privater Termin' : event.subject,
        start: {
          dateTime: event.start.dateTime,
          timeZone: event.start.timeZone
        },
        end: {
          dateTime: event.end.dateTime,
          timeZone: event.end.timeZone
        },
        showAs: event.showAs,
        isPrivate: event.sensitivity === 'private',
        isOrganizer: true
      }));

    console.debug('[Calendar] Filtered events:', {
      totalEvents: filteredEvents.length,
      firstEvent: filteredEvents[0],
      lastEvent: filteredEvents[filteredEvents.length - 1]
    });

    return filteredEvents;
  } catch (error) {
    console.error("[Calendar] Error fetching calendar events:", error);
    throw error;
  }
};

export const createCalendarEvent = async (event: {
  subject: string;
  start: Date;
  end: Date;
  location?: string;
  attendees?: string[];
  description?: string;
  isPrivate?: boolean;
}) => {
  try {
    const graphClient = getGraphClient();
    const timeZone = FIXED_TIME_ZONE;
    
    console.debug('[Calendar] Creating event:', {
      subject: event.subject,
      start: event.start.toISOString(),
      end: event.end.toISOString(),
      timezone: timeZone
    });

    // Create the Microsoft Graph event with the correct time zone handling
    // We need to provide the time in the format: 2023-04-15T09:00:00
    // without the Z suffix which would make it UTC, and explicitly specify the time zone
    
    // Format the date properly for Microsoft Graph API
    const formatDateForGraph = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };
    
    const response = await graphClient.api("/me/events").post({
      subject: event.subject,
      start: {
        dateTime: formatDateForGraph(event.start),
        timeZone: timeZone
      },
      end: {
        dateTime: formatDateForGraph(event.end),
        timeZone: timeZone
      },
      location: event.location ? {
        displayName: event.location,
      } : undefined,
      attendees: event.attendees?.map(email => ({
        emailAddress: {
          address: email,
        },
        type: "required",
      })),
      body: event.description ? {
        contentType: "text",
        content: event.description,
      } : undefined,
      sensitivity: 'normal',
      showAs: 'busy',
      isAllDay: false
    });

    console.debug('[Calendar] Event created:', {
      id: response.id,
      start: response.start,
      end: response.end
    });

    return response;
  } catch (error) {
    console.error("[Calendar] Error creating calendar event:", error);
    throw error;
  }
};

export const updateCalendarEvent = async (eventId: string, updates: {
  subject?: string;
  start?: Date;
  end?: Date;
  location?: string;
  attendees?: string[];
  description?: string;
  isPrivate?: boolean;
}) => {
  try {
    const graphClient = getGraphClient();
    const timeZone = FIXED_TIME_ZONE;
    
    // First, get the current event to preserve existing data
    const currentEvent = await graphClient.api(`/me/events/${eventId}`).get();
    
    // Format the date properly for Microsoft Graph API
    const formatDateForGraph = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };
    
    // Prepare the update payload
    const updatePayload: any = {
      subject: updates.subject ?? currentEvent.subject,
      location: updates.location ? {
        displayName: updates.location,
      } : currentEvent.location,
      body: updates.description ? {
        contentType: "text",
        content: updates.description,
      } : currentEvent.body,
      start: currentEvent.start,
      end: currentEvent.end,
      sensitivity: 'normal',
      isAllDay: false
    };

    // Update start/end times if provided
    if (updates.start) {
      updatePayload.start = {
        dateTime: formatDateForGraph(updates.start),
        timeZone: timeZone
      };
    }
    if (updates.end) {
      updatePayload.end = {
        dateTime: formatDateForGraph(updates.end),
        timeZone: timeZone
      };
    }

    // Update attendees if provided
    if (updates.attendees) {
      // Get current attendees
      const existingAttendees = currentEvent.attendees || [];
      const existingEmails = existingAttendees.map((a: any) => a.emailAddress.address.toLowerCase());
      
      // Add new attendees, avoiding duplicates
      const newAttendees = updates.attendees
        .filter(email => !existingEmails.includes(email.toLowerCase()))
        .map(email => ({
          emailAddress: { address: email },
          type: "required"
        }));

      updatePayload.attendees = [...existingAttendees, ...newAttendees];
    }

    // Update the event
    const response = await graphClient
      .api(`/me/events/${eventId}`)
      .update(updatePayload);

    return response;
  } catch (error) {
    console.error("[Calendar] Error updating calendar event:", error);
    throw error;
  }
};

export const logoutFromMicrosoft = async () => {
  try {
    await msalInstance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error("Error during logout:", error);
    throw error;
  }
};